import React, { createContext, useReducer, useContext, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { CartContextType, CartState, CartAction, CartItem } from '../types/cart';

const CART_STORAGE_KEY = 'racecarly_cart';

const loadInitialState = (): CartState => {
  try {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    return savedCart ? JSON.parse(savedCart) : { items: [] };
  } catch (error) {
    console.error('Error loading cart from storage:', error);
    return { items: [] };
  }
};

const initialState: CartState = loadInitialState();

function cartReducer(state: CartState, action: CartAction): CartState {
  let newState: CartState;
  
  switch (action.type) {
    case 'ADD_ITEM':
      const existingItemIndex = state.items.findIndex(
        item => item.product.id === action.payload.product.id &&
        item.variant?.id === action.payload.variant?.id
      );
      if (existingItemIndex > -1) {
        const newItems = [...state.items];
        newItems[existingItemIndex].quantity += action.payload.quantity;
        newState = { ...state, items: newItems };
      } else {
        newState = { ...state, items: [...state.items, action.payload] };
      }
      break;
      
    case 'REMOVE_ITEM':
      newState = {
        ...state,
        items: state.items.filter(item => 
          item.product.id !== action.payload.productId ||
          item.variant?.id !== action.payload.variant?.id
        )
      };
      break;
      
    case 'UPDATE_QUANTITY':
      newState = {
        ...state,
        items: state.items.map(item =>
          item.product.id === action.payload.productId &&
          item.variant?.id === action.payload.variant?.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        )
      };
      break;
      
    case 'CLEAR_CART':
      newState = { ...state, items: [] };
      break;
      
    default:
      return state;
  }

  // Save to localStorage after each change
  localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(newState));
  return newState;
}

export const CartContext = createContext<CartContextType | null>(null);

interface CartProviderProps {
  children: React.ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const { enqueueSnackbar } = useSnackbar();

  const wrappedDispatch = (action: CartAction) => {
    dispatch(action);
    
    // Show appropriate notifications
    switch (action.type) {
      case 'ADD_ITEM':
        enqueueSnackbar('Item added to cart', {
          variant: 'success',
          autoHideDuration: 2000,
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' }
        });
        break;
      case 'REMOVE_ITEM':
        enqueueSnackbar('Item removed from cart', {
          variant: 'info',
          autoHideDuration: 2000,
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' }
        });
        break;
      case 'CLEAR_CART':
        enqueueSnackbar('Cart cleared', {
          variant: 'info',
          autoHideDuration: 2000,
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' }
        });
        break;
    }
  };
  
  return (
    <CartContext.Provider value={{ state, dispatch: wrappedDispatch }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}; 